/** The deck's live mechanical state, and the per-frame integration of it. */

import type * as THREE from 'three';
import { RPM_33 } from '../geometry/dims.ts';
import { YAW_REST } from '../geometry/tonearm.ts';
import { clamp } from '../lib/math.ts';

export interface DeckState {
  powered: boolean;
  rpm: number;
  /** Pitch fader position, in percent, -8..+8. */
  pitch: number;
  /** Current angular velocity, rad/s. */
  spin: number;
  targetSpin: number;
  /** Platter angle, rad. */
  angle: number;
  progress: number;
  armYaw: number;
  armTargetYaw: number;
  armDown: boolean;
  /** Whether the arm tracks progress automatically, or the user is driving it. */
  armFollow: boolean;
  /** False while a record is mid-swap and not sitting on the spindle. */
  seated: boolean;
  strobePhase: number;
  level: number;
}

export function createDeckState(): DeckState {
  return {
    powered: true,
    rpm: RPM_33,
    pitch: 0,
    spin: 0,
    targetSpin: 0,
    angle: 0,
    progress: 0,
    armYaw: YAW_REST,
    armTargetYaw: YAW_REST,
    armDown: false,
    armFollow: true,
    seated: true,
    strobePhase: 0,
    level: 0,
  };
}

/** Spin-up is brisker than the coast down, the way a direct drive behaves. */
const SPIN_UP = 2.6;
const SPIN_DOWN = 1.9;
const ARM_EASE = 5.0;

/** Platter inertia: approach the target speed, then settle exactly on it. */
export function integrateSpin(s: DeckState, dt: number): void {
  const rate = s.targetSpin > s.spin ? SPIN_UP : SPIN_DOWN;
  s.spin += (s.targetSpin - s.spin) * clamp(dt * rate, 0, 1);
  if (Math.abs(s.targetSpin - s.spin) < 1e-4) s.spin = s.targetSpin;
  /* Records turn clockwise seen from above. */
  s.angle -= s.spin * dt;
}

export function integrateArm(s: DeckState, dt: number): void {
  s.armYaw += (s.armTargetYaw - s.armYaw) * clamp(dt * ARM_EASE, 0, 1);
}

/**
 * Milliseconds until the arm above has eased to within `eps` of its target.
 * The approach is exponential, so the time is the log of the gap — and the
 * per-frame step converges a shade faster than the exponential, never slower.
 */
export function armSettleMs(s: DeckState, eps = 0.006): number {
  const gap = Math.abs(s.armTargetYaw - s.armYaw);
  return gap <= eps ? 0 : Math.ceil((Math.log(gap / eps) / ARM_EASE) * 1000);
}

/**
 * The strobe dots appear to freeze at zero pitch and drift either side of it,
 * exactly as the real illuminator does.
 */
export function strobeOpacity(s: DeckState, dt: number): number {
  if (!s.powered) return 0.06;
  if (s.spin <= 0.01) return 0.18;
  const drift = Math.abs(s.pitch) < 0.05 ? 0 : s.pitch;
  s.strobePhase += dt * (6 + Math.abs(drift) * 3);
  return 0.38 + Math.sin(s.strobePhase) * 0.18;
}

/** A material whose emissive glow can be switched between on and off colours. */
export function setLed(
  material: THREE.MeshStandardMaterial,
  on: boolean,
  color: number,
  offColor: number,
  intensity = 1.5,
): void {
  material.emissiveIntensity = on ? intensity : 0;
  material.color.set(on ? color : offColor);
}
